import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InlineBanner from '../common/InlineBanner.jsx';
import {
    getFileDerivation, linkDerivation, unlinkDerivation,
    establishBaseline, setFileStage, searchFilesByName,
} from '../../services/DerivationService.mjs';
import { kindOf } from '../../utils/fileFacts.js';
import { IconAlert, IconCheck, IconLink, IconPlus, IconSearch, KIND_ICONS } from './icons.jsx';
import '../../css/FileDerivation.css';

const SKIP = { skipGlobalErrorToast: true };
const SEARCH_DEBOUNCE_MS = 200;

const errMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const STAGES = [
    { value: '', label: 'Not set' },
    { value: 'raw', label: 'Raw' },
    { value: 'source', label: 'Source' },
    { value: 'export', label: 'Export' },
];

/**
 * How a relative's freshness reads on screen.
 *
 * The three-way split matters. A link made by pairing two files that already
 * existed genuinely does not know which revision produced which — nobody was
 * watching when it happened — so it reports "baseline not set" rather than
 * either a warning or an all-clear. Showing those as problems would light up
 * almost every file the first time links are created, which is how a warning
 * gets tuned out.
 */
function freshness(rel) {
    if (rel.missing) return { tone: 'gone', label: 'File deleted' };
    if (rel.baselineVersion == null) return { tone: 'unknown', label: 'Baseline not set' };
    if (rel.lag > 0) {
        return { tone: 'stale', label: `${rel.lag} version${rel.lag === 1 ? '' : 's'} behind` };
    }
    return { tone: 'ok', label: 'Up to date' };
}

const RelativeRow = ({ rel, direction, canEdit, busy, onUnlink, onMarkCurrent }) => {
    const state = freshness(rel);
    const Icon = KIND_ICONS[kindOf(rel.mimetype, rel.name)] || KIND_ICONS.file;
    const key = `${direction}:${rel.fileId}`;

    return (
        <li className={`fdv-row fdv-row--${state.tone}`}>
            <span className="fdv-row-icon" aria-hidden="true"><Icon width="15" height="15" /></span>

            <span className="fdv-row-name" title={rel.name || rel.fileId}>
                {rel.missing ? <em>(deleted)</em> : rel.name || rel.fileId}
            </span>

            {!rel.missing && <span className="fdv-row-version">v{rel.versionNumber}</span>}

            <span className={`fdv-state fdv-state--${state.tone}`}>
                {state.tone === 'stale' && <IconAlert width="12" height="12" />}
                {state.tone === 'ok' && <IconCheck width="12" height="12" />}
                {state.label}
            </span>

            {canEdit && (
                <span className="fdv-row-actions">
                    {/* Only offered where it means something: marking the pair
                        current is a claim about THIS file's relationship to its
                        source, so it belongs on the source list, not the export
                        one, where it would be a claim about someone else's file. */}
                    {direction === 'source' && !rel.missing && state.tone !== 'ok' && (
                        <button
                            type="button"
                            className="fdv-action"
                            disabled={busy === key}
                            onClick={() => onMarkCurrent(rel)}
                            title="Record that this file is current with that source"
                        >
                            Mark current
                        </button>
                    )}
                    <button
                        type="button"
                        className="fdv-action fdv-action--danger"
                        disabled={busy === key}
                        onClick={() => onUnlink(rel)}
                        aria-label={`Unlink ${rel.name || rel.fileId}`}
                    >
                        Unlink
                    </button>
                </span>
            )}
        </li>
    );
};

/**
 * A file's place in the authoring pipeline, and the files either side of it.
 *
 * A `.blend` and the `.fbx` exported from it are two ordinary files; this is
 * where the relationship between them is created and read. From either side you
 * can reach the other, and a source that has moved on since its export was made
 * says so.
 *
 * Rendered as a section in the Details tab rather than its own tab, beside
 * Projects — the same class of question ("what is this file related to?"), and a
 * tab that only appeared once links existed could never be used to create the
 * first one. Worth promoting once P4's suggestions and P5's warnings give it
 * more to say.
 */
const FileDerivationSection = ({ file, canEdit = false, onChanged }) => {
    const [view, setView] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(null);
    const [picking, setPicking] = useState(null); // 'source' | 'export' | null
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const pickerRef = useRef(null);
    const searchSeqRef = useRef(0);

    const fileId = file?.id;

    const load = useCallback(async () => {
        if (!fileId) return;
        try {
            setError(null);
            setView(await getFileDerivation(fileId, SKIP));
        } catch (err) {
            setError(errMessage(err, 'Could not load this file’s links.'));
        } finally {
            setLoading(false);
        }
    }, [fileId]);

    useEffect(() => {
        setLoading(true);
        setPicking(null);
        load();
    }, [load]);

    // Close the picker on an outside click or Escape, matching FileProjects.
    useEffect(() => {
        if (!picking) return undefined;
        const onDown = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setPicking(null); };
        const onKey = (e) => { if (e.key === 'Escape') setPicking(null); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [picking]);

    // Ids already spoken for. Offering them would only produce a no-op link, and
    // offering the file ITSELF would produce an error the user can't act on.
    const excluded = useMemo(() => {
        const set = new Set([fileId]);
        (view?.sources ?? []).forEach((r) => set.add(r.fileId));
        (view?.exports ?? []).forEach((r) => set.add(r.fileId));
        return set;
    }, [view, fileId]);

    useEffect(() => {
        if (!picking) { setResults([]); setQuery(''); return undefined; }
        const seq = ++searchSeqRef.current;
        setSearching(true);
        const timer = setTimeout(async () => {
            try {
                const hits = await searchFilesByName(query, { hitsPerPage: 12 }, SKIP);
                if (seq !== searchSeqRef.current) return; // a later keystroke won
                setResults(hits.filter((h) => !excluded.has(h.id)).slice(0, 8));
            } catch {
                if (seq === searchSeqRef.current) setResults([]);
            } finally {
                if (seq === searchSeqRef.current) setSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [query, picking, excluded]);

    /**
     * Every mutation refetches rather than patching local state. The server
     * computes lag live from the other file's current version, so a guess made
     * here would be a second implementation of the rule that decides whether
     * someone sees a warning.
     */
    const run = async (key, work) => {
        setBusy(key);
        setError(null);
        try {
            await work();
            setPicking(null);
            await load();
            await onChanged?.();
        } catch (err) {
            setError(errMessage(err, 'Could not update this file’s links.'));
        } finally {
            setBusy(null);
        }
    };

    const handleLink = (candidate) => {
        const [source, derived] = picking === 'source'
            ? [candidate.id, fileId]
            : [fileId, candidate.id];
        // No `establishBaseline`: linking two files that already exist cannot
        // know which revision produced which. Saying "up to date" here would be
        // a guess presented as a fact.
        return run(`link:${candidate.id}`, () => linkDerivation(source, derived, {}, SKIP));
    };

    const handleUnlink = (rel, direction) => {
        const [source, derived] = direction === 'source' ? [rel.fileId, fileId] : [fileId, rel.fileId];
        return run(`${direction}:${rel.fileId}`, () => unlinkDerivation(source, derived, SKIP));
    };

    const handleMarkCurrent = (rel) =>
        run(`source:${rel.fileId}`, () => establishBaseline(fileId, rel.fileId, SKIP));

    const handleStage = (value) =>
        run('stage', () => setFileStage(fileId, value || null, SKIP));

    const sources = view?.sources ?? [];
    const exports = view?.exports ?? [];
    const staleExports = exports.filter((r) => !r.missing && r.lag > 0).length;

    if (loading) return <div className="fdv-root"><span className="fdv-loading" aria-hidden="true" /></div>;

    return (
        <div className="fdv-root">
            <div className="fdv-stage">
                <label className="fdv-stage-label" htmlFor={`fdv-stage-${fileId}`}>Pipeline stage</label>
                <select
                    id={`fdv-stage-${fileId}`}
                    className="fdv-stage-select"
                    value={view?.stage ?? ''}
                    onChange={(e) => handleStage(e.target.value)}
                    disabled={!canEdit || busy === 'stage'}
                >
                    {STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
            </div>

            {error && <InlineBanner variant="danger" onDismiss={() => setError(null)}>{error}</InlineBanner>}

            {staleExports > 0 && (
                <InlineBanner variant="warning">
                    {staleExports === 1
                        ? '1 file made from this one is out of date.'
                        : `${staleExports} files made from this one are out of date.`}
                </InlineBanner>
            )}

            <div className="fdv-group">
                <h3 className="fdv-group-title">
                    Made from
                    {sources.length > 0 && <span className="fdv-count">{sources.length}</span>}
                </h3>
                {sources.length === 0 ? (
                    <p className="fdv-empty">No source file linked.</p>
                ) : (
                    <ul className="fdv-list">
                        {sources.map((rel) => (
                            <RelativeRow
                                key={rel.fileId}
                                rel={rel}
                                direction="source"
                                canEdit={canEdit}
                                busy={busy}
                                onUnlink={(r) => handleUnlink(r, 'source')}
                                onMarkCurrent={handleMarkCurrent}
                            />
                        ))}
                    </ul>
                )}
                {canEdit && (
                    <button type="button" className="fdv-add" onClick={() => setPicking(picking === 'source' ? null : 'source')} aria-expanded={picking === 'source'}>
                        <IconPlus width="12" height="12" /> Link a source
                    </button>
                )}
            </div>

            <div className="fdv-group">
                <h3 className="fdv-group-title">
                    Exported from this
                    {exports.length > 0 && <span className="fdv-count">{exports.length}</span>}
                </h3>
                {exports.length === 0 ? (
                    <p className="fdv-empty">Nothing has been exported from this file.</p>
                ) : (
                    <ul className="fdv-list">
                        {exports.map((rel) => (
                            <RelativeRow
                                key={rel.fileId}
                                rel={rel}
                                direction="export"
                                canEdit={canEdit}
                                busy={busy}
                                onUnlink={(r) => handleUnlink(r, 'export')}
                                onMarkCurrent={handleMarkCurrent}
                            />
                        ))}
                    </ul>
                )}
                {canEdit && (
                    <button type="button" className="fdv-add" onClick={() => setPicking(picking === 'export' ? null : 'export')} aria-expanded={picking === 'export'}>
                        <IconPlus width="12" height="12" /> Link an exported file
                    </button>
                )}
            </div>

            {/* Two buttons rather than one plus a direction toggle: "which way
                round is this?" is the only thing that can go wrong here, and a
                toggle makes it a setting to notice instead of a choice to make. */}
            {picking && canEdit && (
                <div className="fdv-picker" ref={pickerRef}>
                    <div className="fdv-picker-head">
                        <IconSearch width="13" height="13" />
                        <input
                            className="fdv-search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={picking === 'source' ? 'Find the file this was made from…' : 'Find a file made from this one…'}
                            aria-label={picking === 'source' ? 'Find a source file' : 'Find an exported file'}
                            autoFocus
                        />
                    </div>
                    {searching ? (
                        <p className="fdv-picker-empty">Searching…</p>
                    ) : results.length === 0 ? (
                        <p className="fdv-picker-empty">
                            {query ? 'No matching files.' : 'Type to search for a file.'}
                        </p>
                    ) : (
                        <ul className="fdv-results">
                            {results.map((hit) => {
                                const Icon = KIND_ICONS[kindOf(hit.mimetype, hit.name)] || KIND_ICONS.file;
                                return (
                                    <li key={hit.id}>
                                        <button
                                            type="button"
                                            className="fdv-result"
                                            disabled={busy === `link:${hit.id}`}
                                            onClick={() => handleLink(hit)}
                                        >
                                            <Icon width="14" height="14" />
                                            <span className="fdv-result-name">{hit.name || hit.id}</span>
                                            {hit.stage && <span className="fdv-result-stage">{hit.stage}</span>}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}

            {sources.length === 0 && exports.length === 0 && !canEdit && (
                <p className="fdv-note">
                    <IconLink width="12" height="12" />
                    This file isn’t linked to a source or an export.
                </p>
            )}
        </div>
    );
};

export default FileDerivationSection;
